import {Injectable} from '@angular/core';
import {SynopticFile} from '../../model/synoptics/synoptic-file';
import {FileData, FileErrors} from '../../model/import-csv/file-data';
import {CsvSynopticData} from '../../model/import-csv/csv-synoptic-data';
import {SynopticPersistRequestCsv} from '../../model/import-csv/synoptic-persist-request-csv';
import {CsvErrorList} from '../../model/import-csv/csv-error';
import {CsvSynopticsValidationService} from './csv-synoptics-validation.service';
import {SynopticElementModifier, SynopticElementStatic} from '../../model/synoptics/synoptic-configuration-types';
import {
  SynopticConfigurationComponentCsv,
  SynopticElementEventCsvConfiguration,
  SynopticElementEventPreview,
  SynopticElementStateCsvConfiguration,
  SynopticElementStatePreview,
  SynopticElementStaticCsvConfiguration
} from '../../model/synoptics/synoptic-configuration-csv-types';
import {ShowPopupArgsCsv} from '../../model/synoptics/synoptic-arg-types';

@Injectable({
  providedIn: 'root'
})
export class CsvSynopticsProcessorService {
  public synopticData: CsvSynopticData[] = [];

  constructor(
    private readonly validationService: CsvSynopticsValidationService
  ) {
  }

  public async processFiles(files: File[], svgFiles: SynopticFile[]): Promise<{
    files: FileData[],
    synopticData: CsvSynopticData[]
  }> {
    const filePromises: Promise<FileData>[] = files.map((file: File, index: number) => {
      const fileIdentifier: string = 'id-' + index + '-' + (new Date()).getTime();
      return this.readFile(fileIdentifier, file, svgFiles);
    });

    const processedFiles: FileData[] = await Promise.all(filePromises);

    return {files: processedFiles, synopticData: this.synopticData};
  }

  private readFile(fileIdentifier: string, file: File, svgFiles: SynopticFile[]): Promise<FileData> {
    return new Promise((resolve, reject): void => {
      const reader: FileReader = new FileReader();
      reader.onload = async (): Promise<void> => {
        try {
          const fileReader: string[] = (reader.result as string).split('\n');
          const fileErrors: FileErrors = this.processCsvFile(fileIdentifier, fileReader, svgFiles);
          const fileData: FileData = {fileIdentifier, file, fileErrors, checked: false};
          resolve(fileData);
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = (error: ProgressEvent<FileReader>) => {
        reject(error);
      };
      reader.readAsText(file, 'ISO-8859-3');
    });
  }

  private processCsvFile(fileIdentifier: string, file: string[], svgFiles: SynopticFile[]): FileErrors | null {
    let fileErrors: any = {
      lines: [],
    };

    let number = 0;

    file.forEach((line: string, lineIndex: number): void => {
      if (line.trim() !== '' && !line.toLowerCase().includes('configuration')) {
        const rowData: string[] = line.split(';');
        if (this.validateNumberOfParams(rowData)) {
          const synopticData: SynopticPersistRequestCsv = this.parseRowToSynopticData(rowData);
          let csvSynopticDataData: CsvSynopticData = {
            synopticData,
            fileIdentifier,
            status: 'NONE',
            lineIdentifier: fileIdentifier + synopticData.svg + synopticData.id + synopticData.operation + number++
          };

          const errors: CsvErrorList = this.validateSynopticData(csvSynopticDataData, svgFiles);
          if (errors != null) {
            csvSynopticDataData.status = 'ERROR';
            csvSynopticDataData.errors = errors;
            fileErrors.lines.push(lineIndex);
          } else {
            csvSynopticDataData.status = 'CORRECT';
          }
          this.synopticData.push(csvSynopticDataData);
        } else {
          fileErrors.lines.push(lineIndex);
        }
      }
    });
    return fileErrors.lines.length > 0 ? fileErrors : null;
  }

  private validateNumberOfParams(rowData: string[]): boolean {
    return rowData.length >= 3;
  }

  private parseRowToSynopticData(rowData: string[]): SynopticPersistRequestCsv {
    const [svg, id, operation, configuration] = rowData;
    try {
      const configurationParsed: Object = JSON.parse(configuration);
      return {svg, id, operation, configuration: configurationParsed};
    } catch (error: any) {
      return {svg, id, operation, configuration};
    }
  }

  private validateSynopticData(csvSynopticData: CsvSynopticData, svgFiles: SynopticFile[]): CsvErrorList | null {
    const data: SynopticPersistRequestCsv = csvSynopticData.synopticData;
    const errors: CsvErrorList = this.validationService.validate(svgFiles, data);
    return Object.entries(errors).length > 0 ? errors : null;
  }

  public cleanData(): void {
    this.synopticData = [];
  }

  public removeProcessedData(fileIdentifier: string): CsvSynopticData[] {
    this.synopticData = this.synopticData.filter(data => data.fileIdentifier !== fileIdentifier);
    return this.synopticData;
  }

  public buildConfiguration(): Map<string, SynopticConfigurationComponentCsv> {
    const synopticConfigurationMap: Map<string, SynopticConfigurationComponentCsv> = new Map<string, SynopticConfigurationComponentCsv>();
    let csvId: number = 0;
    this.synopticData.forEach((csvSynopticDataWithMetaData: CsvSynopticData) => {
      const csvSynopticData: SynopticPersistRequestCsv = csvSynopticDataWithMetaData.synopticData;

      let synopticConfiguration: SynopticConfigurationComponentCsv;

      if (synopticConfigurationMap.has(csvSynopticData.svg)) {
        synopticConfiguration = synopticConfigurationMap.get(csvSynopticData.svg);
      } else {
        synopticConfiguration = {
          file: csvSynopticData.svg,
          name: '',
          states: [],
          events: [],
          statics: []
        } as SynopticConfigurationComponentCsv;
      }

      if (this.getOperationType(csvSynopticData.operation) === 'event') {
        const json: SynopticElementEventCsvConfiguration = csvSynopticData.configuration as SynopticElementEventCsvConfiguration;
        if (csvSynopticData.operation === 'showPopup') {
          const argsShowPopup: ShowPopupArgsCsv = json.args as ShowPopupArgsCsv;
          if (synopticConfigurationMap.has(argsShowPopup.file)) {
            synopticConfigurationMap.get(argsShowPopup.file).name = argsShowPopup.component;
          } else {
            const aux: SynopticConfigurationComponentCsv = {
              file: argsShowPopup.file,
              name: argsShowPopup.component,
              states: [],
              events: [],
              statics: []
            };
            synopticConfigurationMap.set(argsShowPopup.file, aux);
          }
        }
        const config: SynopticElementEventPreview = {
          csvId: csvId++,
          id: csvSynopticData.id,
          trigger: json.trigger,
          action: {type: csvSynopticData.operation, args: json.args}
        };
        synopticConfiguration.events.push(config);

      } else if (this.getOperationType(csvSynopticData.operation) === 'states') {
        const json: SynopticElementStateCsvConfiguration = csvSynopticData.configuration as SynopticElementStateCsvConfiguration;
        const config: SynopticElementStatePreview = {
          csvId: csvId++,
          id: csvSynopticData.id,
          sensorIdentifier: json.sensorIdentifier,
          modifier: {type: csvSynopticData.operation, args: json.args} as SynopticElementModifier
        };
        synopticConfiguration.states.push(config);
      } else if (this.getOperationType(csvSynopticData.operation) === 'static') {
        const json: SynopticElementStaticCsvConfiguration = csvSynopticData.configuration as SynopticElementStaticCsvConfiguration;
        const config: SynopticElementStatic = {
          id: csvSynopticData.id,
          attribute: csvSynopticData.operation as 'text',
          value: json.value
        };
        synopticConfiguration.statics.push(config);
      } else {
        return;
      }
      synopticConfigurationMap.set(csvSynopticData.svg, synopticConfiguration);
    });
    return synopticConfigurationMap;
  }

  private getOperationType(operation: string): string {
    switch (operation) {
      case 'executeCommand':
      case 'showPopup':
      case 'closePopup':
      case 'showTooltip':
      case 'linkToSynoptic':
        return 'event';
      case 'styleByThresholds':
      case 'styleByContinuousValue':
      case 'replaceTextValue':
        return 'states';
      case 'text':
        return 'static';
      default:
        throw new Error(`Unsupported operation: ${operation}`);
    }
  }
}
